import { DotLottie } from '@lottiefiles/dotlottie-web';
import { CommonApiService } from '@services/common-api-service';
import { Component, Element, Event, EventEmitter, h, State } from '@stencil/core';
import { pathStore } from '@store/path-store';

@Component({
  tag: 'idle-timer',
  styleUrl: 'idle-timer.scss',
  shadow: true
})
export class IdleTimer {
  @Element() el!: HTMLElement;
  @Event() setSession: EventEmitter<{ reset: boolean; remove: boolean }>;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private lastApiCallTime: number = Date.now();
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  @State() showCountdown: boolean = false;
  @State() countdown: number = 60;
  @State() sessionDuration: number = 14; // minutes
  @State() sessionEnded: boolean = false;
  @State() restarting: boolean = false;
  apiService = new CommonApiService();
  private isCheckingCsrf: boolean = false;

  private async callCheckCsrf() {
    if (this.isCheckingCsrf) return;
    this.isCheckingCsrf = true;
    try {
      await this.apiService.post(
        '/check-csrf-with-auth',
        {
          postData: 'XXXX'
        },
        {
          withCredentials: true
        },
        false
      );
      this.setSession.emit({ reset: true, remove: false });
    } catch (err) {
      console.log(err);
    } finally {
      this.isCheckingCsrf = false;
      this.lastApiCallTime = Date.now();
    }
  }

  private resetTimers = () => {
    if (this.showCountdown) return;
    const now = Date.now();
    const diff = now - this.lastApiCallTime;
    if (diff >= this.sessionDuration * 60 * 1000) {
      this.callCheckCsrf();
    }
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      this.startCountdown();
    }, this.sessionDuration * 60 * 1000);
  };

  private startCountdown = () => {
    this.showCountdown = true;
    this.countdown = 60;
    this.countdownInterval = setInterval(() => {
      this.countdown = this.countdown - 1;
      if (this.countdown === 0) {
        clearInterval(this.countdownInterval!);
        this.sessionEnded = true;
      }
    }, 1000);
    document.documentElement.style.overflow = 'hidden';
  };

  private async handleContinue() {
    await this.callCheckCsrf();
    this.resetTimers();
    this.showCountdown = false;
    document.documentElement.style.overflow = '';
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  }

  componentDidRender() {
    if (this.showCountdown && !this.sessionEnded) {
      const container = this.el.shadowRoot?.querySelector('#lottie-hourglass') as HTMLCanvasElement;
      if (container && !container.dataset.loaded) {
        container.dataset.loaded = 'true'; // Prevent double-loading
        this.loadLottie(container);
      }
    }
  }
  private loadLottie(container: HTMLCanvasElement) {
    const assetPath = pathStore.assetPath;
    const src = `${assetPath}images/lottie/hourglass.lottie`;
    new DotLottie({
      canvas: container,
      autoplay: true,
      loop: true,
      src
    });
  }

  startOver() {
    this.restarting = true;
    this.setSession.emit({ reset: false, remove: true });
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  connectedCallback() {
    const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, this.resetTimers));
    this.resetTimers();
  }

  disconnectedCallback() {
    const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];
    events.forEach(event => window.removeEventListener(event, this.resetTimers));
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  }

  render() {
    if (this.showCountdown)
      return (
        <div class="fixed inset-0 backdrop-blur-[4px] flex items-center justify-center flex-col z-[1000]">
          <div class="bg-white w-80 flex flex-col shadow-2xl justify-end rounded-4xl p-4">
            <div class="flex py-4 gap-4 flex-col items-center">
              <canvas id="lottie-hourglass" class="size-20 pb-2" />
              {this.sessionEnded ? (
                <div class="flex gap-2 flex-col items-center">
                  <h4 class="font-extrabold text-base text-center w-7/10">For Your Safety, We Timed Out the Session</h4>
                  <p class="text-center text-sm">We noticed you stepped away, To protect your details, the session has been closed.</p>
                </div>
              ) : (
                <h2>Session expiring in {this.countdown} seconds</h2>
              )}
            </div>
            <button
              class="w-full idle-timer-btn cursor-pointer py-4 text-white flex justify-center items-center gap-x-2"
              onClick={() => (this.sessionEnded ? this.startOver() : this.handleContinue())}
            >
              {this.restarting ? <span class="spinner" /> : ''}
              {this.sessionEnded ? 'Start Over' : 'Continue'}
            </button>
          </div>
        </div>
      );
  }
}
